/**
 * VehicleRentalRequestPage
 *
 * Mounted at /rentals/vehicle/request. Wizard:
 *   1. Pick a category (chips)
 *   2. Pick a vehicle from that category (cards w/ specs)
 *   3. Fill pickup/dropoff/duration/extras (+ universal Budget block)
 *   4. Submit  → server returns matched providers, we render them.
 *
 * Backend: GET /api/rentals/vehicle/catalog, POST /api/rentals/vehicle/request
 *
 * No hardcoded strings — all labels via i18n. Demo mode (?demo=1) is
 * passed through to the backend so it can include seeded providers.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Truck, Bus, Caravan, Wrench, CheckCircle2, Loader2, MapPin, Star, ChevronLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import GlobalNav from '@/components/GlobalNav';
import { useLanguage } from '@/lib/context';
import BudgetSection from '@/components/BudgetSection';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  van: Truck, truck: Truck, trailer: Caravan, bus: Bus, machine: Wrench,
};

// Subset of countries we have demo data for. The backend accepts any ISO code.
const COUNTRY_OPTIONS = ['NO', 'EE', 'FI', 'SE', 'DK', 'DE', 'PL'];

const fmtSpecValue = (key, val) => {
  if (val === true) return '✓';
  if (val === false) return '–';
  return String(val);
};

const VehicleRentalRequestPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === '1';

  const [catalog, setCatalog] = useState({ categories: [], vehicles: [] });
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState(null);
  const [vehicleId, setVehicleId] = useState(null);
  const [country, setCountry] = useState(searchParams.get('country') || 'NO');
  const [pickupCity, setPickupCity] = useState('');
  const [dropoffCity, setDropoffCity] = useState('');
  const [durationDays, setDurationDays] = useState('3');
  const [distanceKm, setDistanceKm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [extras, setExtras] = useState({
    driver_needed: false, towbar_needed: false, trailer_needed: false,
    child_seat: false, winter_tires: false, insurance_full: false,
  });
  const [flexible, setFlexible] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [budget, setBudget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Fetch catalog once
  useEffect(() => {
    axios.get(`${API}/api/rentals/vehicle/catalog`)
      .then((r) => setCatalog(r.data))
      .catch(() => setCatalog({ categories: [], vehicles: [] }));
  }, []);

  const vehicles = useMemo(
    () => (category ? catalog.vehicles.filter(v => v.category === category) : []),
    [category, catalog.vehicles],
  );
  const selectedVehicle = useMemo(
    () => catalog.vehicles.find(v => v.id === vehicleId) || null,
    [vehicleId, catalog.vehicles],
  );

  const handleSubmit = async () => {
    if (!vehicleId) { setError(t('vehicleRental.errors.pickVehicle')); return; }
    if (!country)   { setError(t('vehicleRental.errors.pickCountry')); return; }
    setError(null);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/rentals/vehicle/request`, {
        vehicle_catalog_id: vehicleId,
        flexible,
        country,
        requirements: {
          pickup_city: pickupCity || null,
          dropoff_city: dropoffCity || null,
          duration_days: durationDays ? parseInt(durationDays, 10) : null,
          distance_km_estimate: distanceKm ? parseFloat(distanceKm) : null,
          start_date: startDate || null,
        },
        extras,
        contact_name: contactName || null,
        contact_phone: contactPhone || null,
        contact_email: contactEmail || null,
        notes: notes || null,
        budget,
        is_demo: isDemo,
      });
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Result page ----------
  if (result) {
    return (
      <div className="min-h-screen bg-slate-50">
        <GlobalNav />
        <div className="max-w-3xl mx-auto px-4 py-8" data-testid="vehicle-rental-success">
          <div className="rounded-2xl border bg-white p-6 space-y-4">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              <h1 className="text-xl font-semibold">{t('vehicleRental.success.title')}</h1>
              {isDemo && (
                <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">
                  {t('vehicleRental.success.demoBadge')}
                </Badge>
              )}
            </div>
            <div className="text-sm text-slate-700">
              <span className="font-medium">{t(`vehicleRental.veh.${result.vehicle_catalog_id}`)}</span>
              {' · '}
              {t(`countries.${result.country}`, { defaultValue: result.country })}
            </div>

            <h2 className="text-base font-semibold pt-2 border-t">
              {t('vehicleRental.success.matchedHeader', { count: result.matched_count })}
            </h2>
            {result.matched_providers.length === 0 ? (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground"
                   data-testid="vehicle-rental-no-match">
                {t('vehicleRental.success.noMatch')}
              </div>
            ) : (
              <ul className="divide-y" data-testid="vehicle-rental-matched-list">
                {result.matched_providers.map((m) => (
                  <li key={m.id} className="py-3 flex items-start justify-between gap-4"
                      data-testid={`vehicle-rental-match-${m.id}`}>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{m.title}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                        <MapPin className="h-3 w-3" /> {m.city}, {m.country}
                        {m.rating ? <span className="flex items-center gap-0.5"><Star className="h-3 w-3 text-amber-500" />{m.rating}</span> : null}
                        {m.is_demo && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">DEMO</Badge>}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {m.matched_vehicle_ids.map((vid) => (
                          <Badge key={vid} variant="secondary" className="text-[10px]">
                            {t(`vehicleRental.veh.${vid}`, { defaultValue: vid })}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <Button variant="outline" onClick={() => { setResult(null); setStep(2); }} data-testid="vehicle-rental-new">
              <ChevronLeft className="h-4 w-4 mr-1" /> {t('vehicleRental.title')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Wizard ----------
  return (
    <div className="min-h-screen bg-slate-50">
      <GlobalNav />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="vehicle-rental-page">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900">
            {t('vehicleRental.title')} {isDemo && <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">DEMO</Badge>}
          </h1>
          <p className="text-sm text-muted-foreground">{t('vehicleRental.subtitle')}</p>
        </header>

        {/* Step 1 — category */}
        <section data-testid="vehicle-rental-step-category"
                 className="rounded-xl border bg-white p-4 space-y-3">
          <h2 className="font-medium text-slate-800">{t('vehicleRental.step.category')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {(catalog.categories || []).map((c) => {
              const Icon = CATEGORY_ICONS[c.id] || Truck;
              const active = category === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setCategory(c.id); setVehicleId(null); setStep(2); }}
                  data-testid={`vehicle-cat-${c.id}`}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition ${
                    active
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-xs font-medium">{t(c.i18n)}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Step 2 — vehicle picker */}
        {category && (
          <section data-testid="vehicle-rental-step-vehicle"
                   className="rounded-xl border bg-white p-4 space-y-3">
            <h2 className="font-medium text-slate-800">{t('vehicleRental.step.vehicle')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {vehicles.map((v) => {
                const active = vehicleId === v.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => { setVehicleId(v.id); setStep(3); }}
                    data-testid={`vehicle-pick-${v.id}`}
                    className={`text-left rounded-lg border p-3 transition ${
                      active
                        ? 'border-slate-900 ring-1 ring-slate-900 bg-slate-50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-slate-900">{t(v.i18n)}</div>
                      {v.license && <Badge variant="outline" className="text-[10px]">{v.license}</Badge>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-slate-600">
                      {Object.entries(v.specs || {}).map(([k, val]) => (
                        <span key={k} className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5">
                          <span className="text-slate-500">{t(`vehicleRental.field.${k}`, { defaultValue: k })}:</span>
                          <span className="font-medium">{fmtSpecValue(k, val)}</span>
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Step 3 — details */}
        {vehicleId && (
          <section data-testid="vehicle-rental-step-details"
                   className="rounded-xl border bg-white p-4 space-y-4">
            <h2 className="font-medium text-slate-800">{t('vehicleRental.step.details')}</h2>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('vehicleRental.country')}</Label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger data-testid="vehicle-rental-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map(c => (
                      <SelectItem key={c} value={c}>{t(`countries.${c}`, { defaultValue: c })}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('vehicleRental.req.pickupCity')}</Label>
                <Input value={pickupCity} onChange={(e) => setPickupCity(e.target.value)}
                       placeholder="Oslo" data-testid="vehicle-rental-pickup-city" />
              </div>
              <div>
                <Label>{t('vehicleRental.req.dropoffCity')}</Label>
                <Input value={dropoffCity} onChange={(e) => setDropoffCity(e.target.value)}
                       data-testid="vehicle-rental-dropoff-city" />
              </div>
              <div>
                <Label>{t('vehicleRental.req.duration_days')}</Label>
                <Input type="number" min={1} value={durationDays}
                       onChange={(e) => setDurationDays(e.target.value)}
                       data-testid="vehicle-rental-duration" />
              </div>
              <div>
                <Label>{t('vehicleRental.req.distance_km')}</Label>
                <Input type="number" min={0} value={distanceKm}
                       onChange={(e) => setDistanceKm(e.target.value)}
                       data-testid="vehicle-rental-distance" />
              </div>
              <div>
                <Label>{t('vehicleRental.req.start_date')}</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                       data-testid="vehicle-rental-start-date" />
              </div>
            </div>

            {/* Extras */}
            <div>
              <Label className="text-sm font-medium">{t('vehicleRental.extras.title')}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {Object.keys(extras).map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer rounded border px-2 py-1.5">
                    <Switch
                      checked={extras[k]}
                      onCheckedChange={(c) => setExtras({ ...extras, [k]: c })}
                      data-testid={`vehicle-rental-extra-${k}`}
                    />
                    <span>{t(`vehicleRental.extras.${k}`)}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Flexible match toggle */}
            <label className="flex items-center justify-between rounded border px-3 py-2">
              <span className="text-sm">{t('vehicleRental.flexibleLabel')}</span>
              <Switch checked={flexible} onCheckedChange={setFlexible}
                      data-testid="vehicle-rental-flexible" />
            </label>

            {/* Universal Budget */}
            <div className="rounded border p-3">
              <BudgetSection
                category="transport"
                country={country}
                value={budget}
                onChange={setBudget}
              />
            </div>

            {/* Contact (optional) */}
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder={t('vehicleRental.contact.name')} value={contactName}
                     onChange={(e) => setContactName(e.target.value)}
                     data-testid="vehicle-rental-contact-name" />
              <Input placeholder={t('vehicleRental.contact.phone')} value={contactPhone}
                     onChange={(e) => setContactPhone(e.target.value)}
                     data-testid="vehicle-rental-contact-phone" />
              <Input placeholder={t('vehicleRental.contact.email')} value={contactEmail}
                     onChange={(e) => setContactEmail(e.target.value)}
                     data-testid="vehicle-rental-contact-email" />
            </div>

            <Textarea placeholder={t('vehicleRental.notes')} value={notes}
                      onChange={(e) => setNotes(e.target.value)} rows={2}
                      data-testid="vehicle-rental-notes" />

            {error && (
              <div className="text-sm text-rose-600 border border-rose-200 bg-rose-50 rounded p-2"
                   data-testid="vehicle-rental-error">
                {error}
              </div>
            )}

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full"
              data-testid="vehicle-rental-submit"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('vehicleRental.submitting')}</>
              ) : t('vehicleRental.submit')}
            </Button>
          </section>
        )}
      </div>
    </div>
  );
};

export default VehicleRentalRequestPage;
